
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Hotel, SearchQuery, HolidayPackage, FlightSegment } from '../types';
import { getImageUrl } from '../utils/imageUtils';
import { useLocalization } from '../hooks/useLocalization';
import { translateFilterValue } from '../constants';
import { fetchBookingData, BookingData } from '../services/apifyService';
import { generateHotelPath } from '../utils/slugUtils';
import { SearchType } from '../types';
import { translateLocationName } from '../utils/localizationUtils';

const StarIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
    <svg className={`w-4 h-4 ${filled ? 'text-yellow-400' : 'text-gray-200'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);

export const HotelCard: React.FC<{ hotel: Hotel; fallbackImage?: string; searchQuery?: SearchQuery }> = ({ hotel, fallbackImage, searchQuery }) => {
    const { t, language } = useLocalization();
    const navigate = useNavigate();
    const [bookingData, setBookingData] = useState<BookingData | null>(null);

    // Fetch Booking.com score via Apify service
    useEffect(() => {
        let mounted = true;
        // Don't fetch if already has data (in a real app, maybe check cache)
        if (!bookingData) {
            fetchBookingData(hotel.name, hotel.location.city, () => { }).then(data => {
                if (mounted && data) setBookingData(data);
            });
        }
        return () => { mounted = false; };
    }, [hotel.name, hotel.location.city]);

    // Prioritize optimized thumbnail, then full image, then fallback
    const displayImage = getImageUrl(hotel.imageUrl, { width: 400, height: 300, mode: 'cover' }) || fallbackImage || '';

    const handleViewDeal = () => {
        navigate(generateHotelPath(hotel), { state: { hotel, searchQuery } });
    };

    // Determine description to show
    let shortDescription = '';

    // 1. Check for explicit Short Description (created from PDF/Admin)
    if (hotel.shortDescription) {
        shortDescription = hotel.shortDescription[language] || hotel.shortDescription.en || hotel.shortDescription.sv || '';
    }

    // 2. Fallback to standard description truncation if no short desc
    if (!shortDescription && hotel.description) {
        const fullDesc = typeof hotel.description === 'string' ? hotel.description : (hotel.description?.[language] || hotel.description?.en || hotel.description?.sv || '');
        if (fullDesc) {
            shortDescription = fullDesc.split('.')[0] + '.'; // Take first sentence
        }
    }

    // 3. Fallback template if absolutely nothing exists
    if (!shortDescription || shortDescription.trim() === "") {
        const city = hotel.location?.city || t('the_resort_area');
        const stars = hotel.rating || 'Top';
        const board = translateFilterValue(hotel.board, language) || t('excellent_service');

        shortDescription = t('hotel_description_template')
            .replace('${hotelName}', hotel.name)
            .replace('${city}', city)
            .replace('${stars}', String(stars))
            .replace('${board}', board);
    }

    const isPackage = searchQuery?.type === SearchType.PackageTours;

    // Extract flight data from first offer (for listing display)
    const hotelWithOffers = hotel as HolidayPackage;
    const firstOffer = hotelWithOffers.offers?.[0];
    const outboundFlight = firstOffer?.outboundFlight;
    const returnFlight = firstOffer?.returnFlight;

    // Helper to format flight time - handle ISO datetime and TBA/placeholder values
    const formatFlightTime = (time: string | undefined): string => {
        if (!time) return 'TBA';
        if (time === 'TBA' || time === '00:00' || time === '24:00' || time.includes('Invalid')) return 'TBA';

        // Handle ISO datetime format (e.g., "2026-05-27T07:55:00")
        if (time.includes('T')) {
            try {
                const date = new Date(time);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleTimeString(language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : 'en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });
                }
            } catch {
                return time.split('T')[1]?.substring(0, 5) || 'TBA';
            }
        }

        return time;
    };

    // Calculate return date from checkIn + nights
    const getReturnDate = (): string => {
        if (!searchQuery?.checkIn || !searchQuery?.nights) return '';
        try {
            const checkIn = new Date(searchQuery.checkIn);
            checkIn.setDate(checkIn.getDate() + searchQuery.nights);
            return checkIn.toLocaleDateString(language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : 'en-US', { day: 'numeric', month: 'short' });
        } catch {
            return '';
        }
    };

    const departureDate = searchQuery?.checkIn
        ? new Date(searchQuery.checkIn).toLocaleDateString(language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : 'en-US', { day: 'numeric', month: 'short' })
        : '';
    const returnDate = getReturnDate();

    // FIX: Use API price directly - NO manual calculation
    // Priority: offer.price > hotel.totalPrice > hotel.pricePerNight (only as absolute fallback)
    const nights = searchQuery?.nights || 1;

    // The API returns the TOTAL package price in offer.price - use it directly
    // firstOffer is already defined above (line 72)
    const apiTotalPrice = firstOffer?.price || (hotel as any).totalPrice;

    // Only use pricePerNight if absolutely no API price available (should never happen with real API data)
    const totalPrice = apiTotalPrice || hotel.pricePerNight || 0;

    // Calculate a "Retail Price" to show a discount (Simulating the strikethrough price in the screenshot)
    const originalPrice = Math.round(totalPrice * 1.18); // 18% higher markup simulation


    return (
        <div className="bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 overflow-hidden flex flex-col md:flex-row transition-all duration-300 group relative">

            {/* Image Section - Fixed size and ratio */}
            <div className="w-full md:w-72 lg:w-80 flex-shrink-0 relative overflow-hidden aspect-[4/3] bg-gradient-to-br from-slate-200 to-slate-300">
                {displayImage ? (
                    <img
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                        src={displayImage}
                        alt={hotel.name}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                    </div>
                )}
            </div>

            {/* Content Section */}
            <div className="p-5 flex-grow flex flex-col justify-between">
                <div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 group-hover:text-gray-600 transition-colors leading-tight">{hotel.name}</h3>
                            <div className="flex items-center mt-2 space-x-2">
                                <div className="flex bg-gray-50 px-1.5 py-0.5 rounded-md">
                                    {[...Array(5)].map((_, i) => <StarIcon key={i} filled={i < hotel.rating} />)}
                                </div>
                                <span className="text-xs font-semibold text-gray-500">{translateLocationName(hotel.location.country, language)} / {translateLocationName(hotel.location.city, language)}</span>
                            </div>
                        </div>

                        {/* Short Description (Right Aligned) */}
                        <div className="hidden lg:block max-w-[260px] text-right">
                            <p className="text-xs text-gray-500 italic leading-relaxed line-clamp-3">
                                {shortDescription}
                            </p>
                        </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                            <svg className="w-3 h-3 mr-1 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                            {hotel.roomType}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                            <svg className="w-3 h-3 mr-1 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" /></svg>
                            {translateFilterValue(hotel.board, language)}
                        </span>
                        {searchQuery?.checkIn && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                <svg className="w-3 h-3 mr-1 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                {searchQuery.checkIn} / {searchQuery.nights} {t('nights')}
                            </span>
                        )}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                        {hotel.themes.slice(0, 3).map(theme => (
                            <span key={theme} className="text-[10px] uppercase tracking-wide bg-amber-50 border border-amber-100 text-amber-700 px-2 py-1 rounded-full font-bold">{translateFilterValue(theme, language)}</span>
                        ))}
                    </div>
                </div>

                {/* Included Services & Price Row */}
                <div className="mt-6">
                    {/* Included Services Row */}
                    {isPackage && (
                        <div className="mb-4 pb-4 border-b border-gray-100">
                            {/* Service Badges Row - flight details are shown on hotel detail page */}
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('included')}:</span>

                                {/* Hotel */}
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 rounded-full border border-indigo-100 text-indigo-700" title="Hotel Stay">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                                        <path fillRule="evenodd" d="M4.5 12a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm6 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm6 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z" clipRule="evenodd" />
                                        <path fillRule="evenodd" d="M2 12V7.75A2.25 2.25 0 014.25 5.5h15.5A2.25 2.25 0 0122 7.75V12h-1.5V9.25a.75.75 0 00-.75-.75H4.25a.75.75 0 00-.75.75V12H2zm0 1.5v6.5h1.5v-3h17v3h1.5v-6.5H2z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-[10px] font-bold">{t('hotel_label')}</span>
                                </div>

                                {/* Flight */}
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-sky-50 rounded-full border border-sky-100 text-sky-700" title="Round-trip Flight">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                                        <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H1.35a.75.75 0 000 1.5h4.997l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                                    </svg>
                                    <span className="text-[10px] font-bold">{t('flights_label')}</span>
                                </div>

                                {/* Transfer */}
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 rounded-full border border-emerald-100 text-emerald-700" title="Airport Transfer">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                                        <path d="M8.25 4.5a3.75 3.75 0 117.5 0v8.25a3.75 3.75 0 11-7.5 0V4.5z" />
                                        <path d="M6 10.5a.75.75 0 01.75.75v1.5a5.25 5.25 0 1010.5 0v-1.5a.75.75 0 011.5 0v1.5a6.751 6.751 0 01-6 6.709v2.291h3a.75.75 0 010 1.5h-7.5a.75.75 0 010-1.5h3v-2.291a6.751 6.751 0 01-6-6.709v-1.5A.75.75 0 016 10.5z" />
                                    </svg>
                                    <span className="text-[10px] font-bold">Transfer</span>
                                </div>

                                {/* Baggage */}
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 rounded-full border border-orange-100 text-orange-700" title="Checked Baggage">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                                        <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 006 5.25v1.5h12V5.25a1.5 1.5 0 00-1.5-1.5h-9zM5 10.5a3 3 0 013-3h8a3 3 0 013 3v6a3 3 0 01-3 3H8a3 3 0 01-3-3v-6zM8.25 12A.75.75 0 019 11.25h6a.75.75 0 010 1.5H9A.75.75 0 018.25 12z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-[10px] font-bold">{t('baggage')}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-end justify-between">
                        <div>
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wide mb-0.5">
                                {t('total_price_for_nights').replace('{0}', String(nights))}
                            </p>

                            {/* Strikethrough Original Price Simulation */}
                            <div className="flex flex-col">
                                <span className="text-xs text-red-400 line-through font-medium">
                                    {originalPrice.toLocaleString(language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {hotel.currency}
                                </span>
                                <div className="flex items-baseline">
                                    <span className="text-3xl font-extrabold text-gray-900">
                                        {totalPrice.toLocaleString(language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </span>
                                    <span className="text-sm font-bold text-gray-500 ml-1.5">{hotel.currency}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                            {/* Social Buttons Row */}
                            <div className="flex items-center gap-2">
                                {/* Instagram Follow Button - Links to hotel's Instagram */}
                                {hotel.instagramUrl && (
                                    <a
                                        href={hotel.instagramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-[10px] font-bold hover:from-purple-600 hover:to-pink-600 transition-all shadow-sm"
                                        title={language === 'sv' ? 'Följ hotellet på Instagram' : language === 'da' ? 'Følg hotellet på Instagram' : 'Follow hotel on Instagram'}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                        </svg>
                                        {t('follow') || 'Följ'}
                                    </a>
                                )}

                                {/* Booking.com Score Badge */}
                                {bookingData && (
                                    <div className="flex items-center gap-2 bg-blue-50 px-2 py-1 rounded border border-blue-100">
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/b/be/Booking.com_logo.svg" alt="Booking.com" className="h-3 opacity-80 grayscale" />
                                        <div className="bg-blue-900 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                                            {bookingData.score.toFixed(1)}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleViewDeal}
                                className="bg-blue-950 hover:bg-blue-900 text-white font-bold py-3 px-8 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm flex items-center gap-2 group-hover:translate-x-1"
                            >
                                {t('view_deal')}
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};
